// rulesConfig.ts - Default rules configuration template with types
//
// Field properties:
// - desc: Descriptive help text shown in UI (tooltip/subtitle)
// - disabled: UI greyed out, can't interact
// - required: Must have value before save
// - locked: Needs addon purchase to unlock (paywall)
// - active: Rule applies during gameplay
// - hidden: Not shown in UI at all
// - frozen: Cannot edit after contest starts/publishes
// - available: Whether this field is available for the series (e.g. set false to hide for specific series)
// - dependsOn: { key, value } - Show field/section only when another field matches value

// Types
export type FieldType = 'number' | 'boolean' | 'string' | 'select' | 'range' | 'minMaxRange' | 'transferWindows' | 'datetime';

export interface MinMaxValue {
  min: number;
  max: number;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldConstraints {
  min?: number;
  max?: number;
  step?: number;
}

export interface DependsOn {
  key: string;
  value: string | number | boolean;
}

// Transfer Window Types
export type TransferStage = 'entire_series' | 'every_match' | 'mid_series' | 'custom';
export interface TransferWindow {
  id: string;
  stage: TransferStage;
  maxTransfers: number | null;
  freeTransfers: number | null;
  penaltyPoints: number | null;
  fromMatch?: number | null;
  toMatch?: number | null;
  label?: string | null;
}

export interface TransferWindowItemSchema {
  stage: {
    type: 'select';
    options: SelectOption[];
  };
  maxTransfers: {
    type: 'number';
    label: string;
    constraints: FieldConstraints;
  };
  fromMatch: {
    type: 'number';
    label: string;
    constraints: FieldConstraints;
  };
  toMatch: {
    type: 'number';
    label: string;
    constraints: FieldConstraints;
  };
  label: {
    type: 'string';
    label: string;
  };
}

export interface TransferWindowItemDefault {
  stage: string;
  maxTransfers: number | null;
  freeTransfers: number | null;
  penaltyPoints: number | null;
  fromMatch: number | null;
  toMatch: number | null;
  label: string | null;
  available: boolean;
}

export type FieldValue = number | boolean | string | MinMaxValue | TransferWindow[] | { stage: string } | null;

export interface RuleField {
  key: string;
  label: string;
  desc: string;
  type: FieldType;
  default: FieldValue;
  value?: FieldValue;
  constraints?: FieldConstraints;
  // Legacy support - direct min/max/step/unit
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: SelectOption[];
  dependsOn?: DependsOn;
  // For transferWindows type
  itemSchema?: TransferWindowItemSchema;
  itemDefault?: TransferWindowItemDefault;
  // Field states
  disabled: boolean;
  required: boolean;
  locked: boolean;
  active: boolean;
  hidden: boolean;
  frozen: boolean;
  available: boolean;
}

export interface RuleSection {
  section: string;
  fields: RuleField[];
  notes?: string[];
  locked: boolean;
  dependsOn?: DependsOn;
}

export interface RulesConfig {
  rulesConfig: RuleSection[];
}

// Helper functions for manipulating rules config

/**
 * Get a field by key from any section
 */
export const getFieldByKey = (config: RuleSection[], key: string): RuleField | undefined => {
  for (const section of config) {
    const field = section.fields.find(f => f.key === key);
    if (field) return field;
  }
  return undefined;
};

/**
 * Get a section by name
 */
export const getSectionByName = (config: RuleSection[], name: string): RuleSection | undefined => {
  return config.find(s => s.section.toLowerCase() === name.toLowerCase());
};

/**
 * Update a field value by key
 */
export const updateFieldValue = (
  config: RuleSection[],
  key: string,
  value: FieldValue
): RuleSection[] => {
  return config.map(section => ({
    ...section,
    fields: section.fields.map(field =>
      field.key === key ? { ...field, value } : field
    )
  }));
};

/**
 * Add a new field to a section
 */
export const addFieldToSection = (
  config: RuleSection[],
  sectionName: string,
  field: RuleField
): RuleSection[] => {
  return config.map(section => {
    if (section.section !== sectionName) return section;
    return {
      ...section,
      fields: [...section.fields, field]
    };
  });
};

/**
 * Remove a field from a section
 */
export const removeFieldFromSection = (
  config: RuleSection[],
  sectionName: string,
  fieldKey: string
): RuleSection[] => {
  return config.map(section => {
    if (section.section !== sectionName) return section;
    return {
      ...section,
      fields: section.fields.filter(f => f.key !== fieldKey)
    };
  });
};

/**
 * Add a new section
 */
export const addSection = (
  config: RuleSection[],
  section: RuleSection
): RuleSection[] => {
  return [...config, section];
};

/**
 * Remove a section by name
 */
export const removeSection = (
  config: RuleSection[],
  sectionName: string
): RuleSection[] => {
  return config.filter(s => s.section !== sectionName);
};

/**
 * Update section properties
 */
export const updateSection = (
  config: RuleSection[],
  sectionName: string,
  updates: Partial<RuleSection>
): RuleSection[] => {
  return config.map(section =>
    section.section === sectionName ? { ...section, ...updates } : section
  );
};

/**
 * Create a new field with defaults
 */
export const createField = (partial: Partial<RuleField> & { key: string; label: string; desc: string; type: FieldType }): RuleField => {
  const defaults: Omit<RuleField, 'key' | 'label' | 'desc' | 'type'> = {
    default: partial.type === 'boolean' ? false :
             partial.type === 'minMaxRange' ? { min: 0, max: 0 } :
             partial.type === 'number' || partial.type === 'range' ? 0 :
             '',
    disabled: false,
    required: false,
    locked: false,
    active: true,
    hidden: false,
    frozen: false,
    available: true,
  };

  return { ...defaults, ...partial } as RuleField;
};

/**
 * Create a new section with defaults
 */
export const createSection = (name: string, dependsOn?: DependsOn): RuleSection => ({
  section: name,
  dependsOn,
  fields: [],
  notes: [],
  locked: false,
});

/**
 * Validate a rules config
 */
export const validateRulesConfig = (config: RuleSection[]): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  for (const section of config) {
    if (!section.section) {
      errors.push('Section name is required');
    }

    const fieldKeys = new Set<string>();
    for (const field of section.fields) {
      if (!field.key) {
        errors.push(`Field in section "${section.section}" is missing a key`);
      }
      if (!field.label) {
        errors.push(`Field "${field.key}" in section "${section.section}" is missing a label`);
      }
      if (fieldKeys.has(field.key)) {
        errors.push(`Duplicate field key "${field.key}" in section "${section.section}"`);
      }
      fieldKeys.add(field.key);

      if (field.required && (field.value === null || field.value === undefined || field.value === '')) {
        errors.push(`Required field "${field.label}" in section "${section.section}" has no value`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Get constraints from field
 */
export const getFieldConstraints = (field: RuleField): FieldConstraints => {
  return field.constraints || {};
};

/**
 * Deep clone a rules config
 */
export const cloneRulesConfig = (config: RuleSection[]): RuleSection[] => {
  return JSON.parse(JSON.stringify(config));
};

/**
 * Reset all field values to their defaults
 */
export const resetToDefaults = (config: RuleSection[]): RuleSection[] => {
  return config.map(section => ({
    ...section,
    fields: section.fields.map(field => ({
      ...field,
      value: field.default,
    }))
  }));
};

/**
 * Get only active, non-hidden, and available fields
 */
export const getVisibleFields = (section: RuleSection): RuleField[] => {
  return section.fields.filter(f => f.active && !f.hidden && f.available);
};

/**
 * Check if any field is frozen (for published contests)
 */
export const hasEditableFields = (config: RuleSection[], isPublished: boolean): boolean => {
  for (const section of config) {
    for (const field of section.fields) {
      if (!field.hidden && field.active && !field.disabled && field.available) {
        if (!isPublished || !field.frozen) {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * Check if a section's dependsOn condition is met
 */
export const isSectionVisible = (config: RuleSection[], section: RuleSection): boolean => {
  if (!section.dependsOn) return true;
  const field = getFieldByKey(config, section.dependsOn.key);
  if (!field) return false;
  return field.value === section.dependsOn.value;
};

/**
 * Check if a field's dependsOn condition is met
 */
export const isFieldVisible = (config: RuleSection[], field: RuleField): boolean => {
  if (!field.dependsOn) return true;
  const dependentField = getFieldByKey(config, field.dependsOn.key);
  if (!dependentField) return false;
  return dependentField.value === field.dependsOn.value;
};

// Default rules configuration
export const defaultRulesConfig: RuleSection[] = [
  {
    section: "Rules",
    locked: false,
    fields: [
      {
        key: "max_members",
        label: "Max Members",
        desc: "Maximum number of participants allowed in this contest.",
        type: "number",
        default: 10,
        value: 10,
        constraints: { min: 2, max: 10 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "max_teams_per_member",
        label: "Max Teams Per Member",
        desc: "How many fantasy teams each member can create in this contest.",
        type: "number",
        default: 1,
        value: 1,
        constraints: { min: 1, max: 5 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "players_per_team",
        label: "Players Per Team",
        desc: "Number of players each fantasy team must have.",
        type: "number",
        default: 11,
        value: 11,
        constraints: { min: 1, max: 11 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "player_selection_format",
        label: "Player Selection Format",
        desc: "How members pick players. Open: free pick. Draft: take turns. Auction: bid with virtual currency.",
        type: "select",
        default: "open",
        value: "open",
        options: [
          { value: "open", label: "Open Selection" },
          { value: "draft", label: "Snake Draft" },
          { value: "auction", label: "Auction" },
        ],
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: true,
        available: true,
      },
    ],
  },
  {
    section: "Auction",
    locked: false,
    dependsOn: { key: "player_selection_format", value: "auction" },
    fields: [
      {
        key: "starting_budget",
        label: "Starting Budget",
        desc: "Virtual currency each member gets to bid on players. Higher budgets allow more competitive bidding.",
        type: "number",
        default: 100,
        value: 100,
        constraints: { min: 10, max: 10000 },
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: true,
        available: true,
      },
      {
        key: "base_player_price",
        label: "Base Player Price",
        desc: "Minimum price for any player. Bidding starts at this amount and unsold players are auto-assigned at this price.",
        type: "number",
        default: 1,
        value: 1,
        constraints: { min: 1, max: 100 },
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: true,
        available: true,
      },
      {
        key: "min_bid_increment",
        label: "Minimum Bid Increment",
        desc: "Smallest amount a new bid must exceed the current bid by. Prevents tiny bid increases.",
        type: "number",
        default: 1,
        value: 1,
        constraints: { min: 1, max: 50 },
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: true,
        available: true,
      },
      {
        key: "bid_timer_seconds",
        label: "Bid Timer (seconds)",
        desc: "Time allowed for bidding on each player. When the timer runs out, the highest bidder wins.",
        type: "number",
        default: 30,
        value: 30,
        constraints: { min: 10, max: 120 },
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "anti_snipe_seconds",
        label: "Bid Protection Window (sec)",
        desc: "If a bid is placed within this many seconds of the timer ending, the timer gets extended to prevent last-second sniping.",
        type: "number",
        default: 10,
        value: 10,
        constraints: { min: 5, max: 30 },
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "anti_snipe_extension",
        label: "Bid Protection Extension (sec)",
        desc: "How many extra seconds are added to the timer when a last-second bid triggers the bid protection.",
        type: "number",
        default: 15,
        value: 15,
        constraints: { min: 5, max: 60 },
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "scheduled_start_at",
        label: "Scheduled Auction Start Time",
        desc: "Set a date and time for the auction to begin. Members can join the waiting room before this time.",
        type: "datetime",
        default: null,
        value: null,
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "auto_start",
        label: "Auto-Start Auction",
        desc: "When enabled, the auction starts automatically at the scheduled time. Otherwise, the owner must start it manually.",
        type: "boolean",
        default: false,
        value: false,
        dependsOn: { key: "player_selection_format", value: "auction" },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
  },
  {
    section: "Role Limits",
    locked: false,
    fields: [
      {
        key: "role_limits_allowed",
        label: "Allow Role Limits",
        desc: "Enable minimum and maximum limits on player roles (batsmen, bowlers, etc.) in each team.",
        type: "boolean",
        default: false,
        value: false,
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "batsmen",
        label: "Batsmen",
        desc: "Set the minimum and maximum number of batsmen allowed in each team.",
        type: "minMaxRange",
        default: { min: 0, max: 11 },
        value: { min: 0, max: 11 },
        constraints: { min: 0, max: 11 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "bowlers",
        label: "Bowlers",
        desc: "Set the minimum and maximum number of bowlers allowed in each team.",
        type: "minMaxRange",
        default: { min: 0, max: 11 },
        value: { min: 0, max: 11 },
        constraints: { min: 0, max: 11 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "wicket_keepers",
        label: "Wicket Keepers",
        desc: "Set the minimum and maximum number of wicket keepers allowed in each team.",
        type: "minMaxRange",
        default: { min: 0, max: 11 },
        value: { min: 0, max: 11 },
        constraints: { min: 0, max: 11 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "all_rounders",
        label: "All Rounders",
        desc: "Set the minimum and maximum number of all-rounders allowed in each team.",
        type: "minMaxRange",
        default: { min: 0, max: 11 },
        value: { min: 0, max: 11 },
        constraints: { min: 0, max: 11 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "overseas_players",
        label: "Overseas Players",
        desc: "Set the minimum and maximum number of overseas players allowed in each team. Only applies to league series with a host country configured.",
        type: "minMaxRange",
        default: { min: 0, max: 11 },
        value: { min: 0, max: 11 },
        constraints: { min: 0, max: 11 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
  },
  {
    section: "Transfers",
    locked: false,
    fields: [
      {
        key: "transfers_allowed",
        label: "Allow Transfers",
        desc: "Enable player transfers so members can swap players in their team during the series.",
        type: "boolean",
        default: false,
        value: false,
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "transfer_windows",
        label: "Transfer Windows",
        desc: "Define when transfers can happen and how many are allowed in each window.",
        type: "transferWindows",
        default: [],
        value: [],
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
        itemSchema: {
          stage: {
            type: 'select',
            options: [
              { value: 'entire_series', label: 'Entire Series' },
              { value: 'every_match', label: 'Before Every Match' },
              { value: 'mid_series', label: 'Mid Series' },
              { value: 'custom', label: 'Custom Range' },
            ],
          },
          maxTransfers: {
            type: 'number',
            label: 'Max Transfers',
            constraints: { min: 0, max: 100 },
          },
          fromMatch: {
            type: 'number',
            label: 'From Match',
            constraints: { min: 1 },
          },
          toMatch: {
            type: 'number',
            label: 'To Match',
            constraints: { min: 1 },
          },
          label: {
            type: 'string',
            label: 'Window Label',
          },
        },
        itemDefault: {
          stage: "entire_series",
          maxTransfers: null,
          freeTransfers: null,
          penaltyPoints: null,
          fromMatch: null,
          toMatch: null,
          label: null,
          available: true,
        },
      },
    ],
  },
  {
    section: "Captaincy",
    locked: false,
    fields: [
      {
        key: "captaincy_bonus",
        label: "Captain & Vice-Captain Bonus",
        desc: "Award bonus points to your captain (2x) and vice-captain (1.5x) for their performance.",
        type: "boolean",
        default: true,
        value: true,
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "captain_changes_allowed",
        label: "Allow Captain/VC Changes",
        desc: "Let members change their captain and vice-captain picks during the series.",
        type: "boolean",
        default: true,
        value: true,
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
  },
  {
    section: "Leaderboard",
    locked: false,
    fields: [
      {
        key: "best_of_players",
        label: "Best Of (Top N Players Score)",
        desc: "Only count points from your top N performing players instead of all. Set to 0 to count all players.",
        type: "number",
        default: 0,
        value: 0,
        constraints: { min: 0, max: 20 },
        disabled: false,
        required: false,
        locked: true,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "best_of_overseas",
        label: "Best Of Overseas (Top N Overseas Score)",
        desc: "Only count points from your top N overseas players. Remaining overseas players' points are excluded. Set to 0 to count all overseas players. Applied after Best Of Players.",
        type: "number",
        default: 0,
        value: 0,
        constraints: { min: 0, max: 11 },
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
  },
];

// Helper to create a new transfer window with defaults
export const createTransferWindow = (partial?: Partial<TransferWindow>): TransferWindow => ({
  id: `tw_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  stage: 'entire_series',
  maxTransfers: null,
  freeTransfers: null,
  penaltyPoints: null,
  ...partial,
});

// Get stage label
export const getStageLabel = (stage: TransferStage): string => {
  const labels: Record<string, string> = {
    entire_series: 'Entire Series',
    every_match: 'Before Every Match',
    mid_series: 'Mid Series',
    custom: 'Custom Range',
  };
  return labels[stage] || stage;
};

export default defaultRulesConfig;
